import api from '@/services/api';
import { mapActions, mapGetters } from 'vuex';

const ACTION_PREDUGOVOR   = 182400033;
const ACTION_TERMS_MOBILE = 182400000;
const ACTION_TERMS_TV     = 182400015;
import ConfirmationModal from '@/components/Partials/ConfirmationModal/ConfirmationModal.vue';
import AddonConfirmationModal from '@/components/Partials/AddonConfirmationModal/AddonConfirmationModal.vue';
import InsufficientFoundModal from '@/components/Partials/InsufficientFoundModal/InsufficientFoundModal.vue';
import SuccessModal from '@/components/Partials/SuccessModal/SuccessModal.vue';
import router from '@/router';
import Package from './Package/Package.vue';

export default {
    components: {
        Package,
        ConfirmationModal,
        AddonConfirmationModal,
        InsufficientFoundModal,
        SuccessModal,
    },

    data() {
        return {
            packages: {
                data: [],
                bonus: [],
                roaming: [],
            },
            draftAddonLines: [],
            showInsufficientFoundModal: false,
            showSuccessModal: false,
            showAddonConfirmationModal: false,
            addonReports: null,
            addonContractId: null,
            isAddonLoading: false,
            isPackagesLoading: false,
            modalMessage: "",
            bundleId: null,
            isPackageButtonClicked: false,
            packageAction: ""
        };
    },

    computed: {
        ...mapGetters({
            locale: 'locale',
            contract: 'contract/contract',
        }),

        hasDraftAddonLines() {
            return this.draftAddonLines.length > 0;
        },

        hasDeezerActiveOrDraft() {
            const deezerActive = this.packages.bonus.some(
                p => p.isAddon && p.name && p.name.toLowerCase().includes('deezer') && p.active
            );
            const deezerDraft = this.draftAddonLines.some(
                l => l.log_name && l.log_name.toLowerCase().includes('deezer')
            );
            return deezerActive || deezerDraft;
        },

        draftLinesByPackageName() {
            const map = {};
            this.draftAddonLines.forEach(line => {
                if (!line.log_name) return;
                if (!map[line.log_name]) map[line.log_name] = line;
            });
            return map;
        },
    },

    watch: {
        /**
         * When language is changed, then call again api call with new locale
         */
        locale() {
            this.getPackages();
        },

        /**
         * Listen for object changed for clicked contract
         */
        'contract.clickedContract.phoneNumber': {
            handler() {
                this.getPackages();
            },
            deep: true,
        },
    },

    methods: {
        ...mapActions('user', ['setUserDataAfterUpdating']),

        /**
         * Get all packages on component mounted
         */
        getPackages() {
            this.$Progress.start();
            this.isPackagesLoading = true;
            Promise.all([
                api.get('/api/bundles'),
                api.get('/api/addon-contract-lines'),
            ])
                .then(([bundlesRes, linesRes]) => {
                    this.packages.data   = bundlesRes.data.body.bundles.data;
                    this.packages.bonus  = bundlesRes.data.body.bundles.bonus;
                    this.packages.roaming = bundlesRes.data.body.bundles.roaming;
                    this.draftAddonLines = (linesRes.data.body.lines || []).filter(
                        l => l.statuscode_label === 'Draft'
                    );
                    this.$Progress.finish();
                    this.isPackagesLoading = false;
                })
                .catch(() => {
                    this.$toast.error(this.$i18n.t('messages.error'));
                    this.$Progress.finish();
                    this.isPackagesLoading = false;
                });
        },


        /**
         * Send bundle ID to our server then
         */
        sendPackageIdAndOpenModalForm() {
            switch (this.packageAction) {
                case 'activate':
                    this.activatePackage();
                    break;
                case 'deactivate':
                    this.deactivatePackage();
                    break;
                default:
                    break;
            }
        },

        /**
         * Send bundle ID to deactivate
         */
        deactivatePackage() {
            this.isPackageButtonClicked = false;
            this.packageAction = "";
            this.isAddonLoading = true;

            const params = new URLSearchParams();
            params.append('plan_id', this.bundleId);
            params.append('type', 'bundle');
            api.post(`/api/bundles/deactivate/${this.bundleId}`, params)
                .then(() => {
                    this.isAddonLoading = false;
                    this.$Progress.finish();
                    this.$toast.success(this.$i18n.t('messages.success'));
                    this.getPackages();
                }).catch(() => {
                    this.isAddonLoading = false;
                    this.$toast.error(this.$i18n.t('messages.error'));
                    this.$Progress.finish();
                });
        },

        /**
         * Send bundle ID to activate (regular packages)
         */
        activatePackage() {
            this.isPackageButtonClicked = false;
            this.packageAction = "";

            const params = new URLSearchParams();
            params.append('plan_id', this.bundleId);
            params.append('type', 'bundle');
            api.post(`/api/bundles/activate/${this.bundleId}`, params)
                .then(() => {
                    this.$Progress.finish();
                    this.showSuccessModal = true;
                    this.getPackages();
                    this.setUserDataAfterUpdating();
                }).catch((error) => {
                    if (error.response.data.code === 'ACTIVATE_BUNDLE_FAILED_INSUFFICIENT_FOUND') {
                        this.showInsufficientFoundModal = true;
                        this.modalMessage = error.response.data.body.message;
                    } else {
                        this.$toast.error(this.$i18n.t('messages.error'));
                    }
                    this.$Progress.finish();
                });
        },

        /**
         * Activate a Deezer / TV Go addon, then fetch contract reports sequentially
         * before opening the addon-specific confirmation modal with PDF links.
         */
        activateAndLoadAddonModal(bundleId) {
            if (this.isAddonLoading) return;

            this.$Progress.start();
            this.isAddonLoading = true;

            const activateParams = new URLSearchParams();
            activateParams.append('plan_id', bundleId);
            activateParams.append('type', 'bundle');

            api.post(`/api/bundles/activate/${bundleId}`, activateParams)
                .then((activateResponse) => {
                    const contractId = activateResponse.data.body?.contractId;
                    this.addonContractId = contractId;
                    const termsAction = this.contract.clickedContract?.isTv ? ACTION_TERMS_TV : ACTION_TERMS_MOBILE;

                    return api.post('/api/get-contract-report', { orderId: contractId, action: ACTION_PREDUGOVOR })
                        .then((contractReportRes) => {
                            return api.post('/api/get-contract-report', { orderId: contractId, action: termsAction })
                                .then((termsReportRes) => [contractReportRes, termsReportRes]);
                        });
                })
                .then(([contractReportRes, termsReportRes]) => {
                    this.$Progress.finish();

                    const contractBody = contractReportRes.data.body;
                    const termsBody = termsReportRes.data.body;

                    this.addonReports = {
                        contract: contractBody?.ReportsExist && contractBody.OutputData?.length > 0
                            ? contractBody.OutputData[0] : null,
                        terms: termsBody?.ReportsExist && termsBody.OutputData?.length > 0
                            ? termsBody.OutputData[0] : null,
                    };

                    const pkg = this.packages.bonus.find(p => p.bundleCode === bundleId);
                    if (pkg) {
                        this.draftAddonLines = [{ log_name: pkg.name, log_contractlineid: null, statuscode_label: 'Draft' }];
                    }

                    this.isAddonLoading = false;
                    this.showAddonConfirmationModal = true;
                })
                .catch(() => {
                    this.isAddonLoading = false;
                    this.$Progress.finish();
                    this.$toast.error(this.$i18n.t('messages.addonActivatedReportFailed'));
                    this.getPackages();
                });
        },

        isAddonActivateDisabled(pkg) {
            if (!pkg.isAddon) return false;
            const isDeezer = pkg.name && pkg.name.toLowerCase().includes('deezer');
            if (!isDeezer) return false;
            return this.hasDeezerActiveOrDraft;
        },

        getDraftForPackage(packageName, productId) {
            if (!this.hasDraftAddonLines) return null;
            if (productId) {
                const byCode = this.draftAddonLines.find(l => l.product_code === productId);
                if (byCode) return byCode;
            }
            if (!packageName) return null;
            const name = packageName.toLowerCase();
            return this.draftAddonLines.find(l => {
                if (!l.log_name) return false;
                const logName = l.log_name.toLowerCase();
                return logName.startsWith(name) || name.startsWith(logName);
            }) || null;
        },

        /**
         * Resume activation of an existing draft addon contract line.
         * Skips the activate call since the contract line already exists in CRM.
         */
        resumeDraftAddon(draftLine) {
            if (this.isAddonLoading) return;

            this.$Progress.start();
            this.isAddonLoading = true;

            const contractId  = this.contract.clickedContract?.logosoftContractId;
            const termsAction = this.contract.clickedContract?.isTv ? ACTION_TERMS_TV : ACTION_TERMS_MOBILE;
            this.addonContractId = contractId;

            api.post('/api/get-contract-report', { orderId: contractId, action: ACTION_PREDUGOVOR })
                .then((contractReportRes) => {
                    return api.post('/api/get-contract-report', { orderId: contractId, action: termsAction })
                        .then((termsReportRes) => [contractReportRes, termsReportRes]);
                })
                .then(([contractReportRes, termsReportRes]) => {
                    this.$Progress.finish();

                    const contractBody = contractReportRes.data.body;
                    const termsBody    = termsReportRes.data.body;

                    this.addonReports = {
                        contract: contractBody?.ReportsExist && contractBody.OutputData?.length > 0
                            ? contractBody.OutputData[0] : null,
                        terms: termsBody?.ReportsExist && termsBody.OutputData?.length > 0
                            ? termsBody.OutputData[0] : null,
                    };

                    this.isAddonLoading = false;
                    this.showAddonConfirmationModal = true;
                })
                .catch(() => {
                    this.isAddonLoading = false;
                    this.$Progress.finish();
                    this.$toast.error(this.$i18n.t('messages.error'));
                });
        },

        deleteDraftAddon(lineId) {
            if (this.isAddonLoading) return;

            this.isAddonLoading = true;
            this.$Progress.start();

            api.post('/api/deactivate-draft-addon-line', { lineId })
                .then(() => {
                    this.isAddonLoading = false;
                    this.$Progress.finish();
                    this.$toast.success(this.$i18n.t('messages.draftDeleted'));
                    this.getPackages();
                })
                .catch(() => {
                    this.isAddonLoading = false;
                    this.$Progress.finish();
                    this.$toast.error(this.$i18n.t('messages.error'));
                });
        },

        /**
         * Sign the addon contract after user confirms in the modal
         */
        signAddonContract() {
            const contractId = this.addonContractId;
            this.showAddonConfirmationModal = false;
            this.addonReports = null;
            this.addonContractId = null;
            this.isAddonLoading = true;
            this.$Progress.start();

            const params = new URLSearchParams();
            params.append('draftId', contractId);
            params.append('consentTerm', 'Contract');
            params.append('consentNote', 'self care addon activation');

            api.post('/api/sign-contract', params)
                .then(() => {
                    this.isAddonLoading = false;
                    this.$Progress.finish();
                    this.showSuccessModal = true;
                    this.getPackages();
                    this.setUserDataAfterUpdating();
                })
                .catch(() => {
                    this.isAddonLoading = false;
                    this.$toast.error(this.$i18n.t('messages.error'));
                    this.$Progress.finish();
                });
        },

        /**
         * Close showInsufficientFoundModal modal
         */
        closeModal() {
            this.showInsufficientFoundModal = false;
            this.modalMessage = "";
        },

        /**
         * Close showSuccessModal modal
         */
        closeSuccessModal() {
            this.showSuccessModal = false;
        },

        /**
         * Close addon confirmation modal
         */
        closeAddonConfirmationModal() {
            this.showAddonConfirmationModal = false;
            this.addonReports = null;
            this.addonContractId = null;
            this.getPackages();
        },

        /**
         * Open confirmation modal (regular packages)
         */
        openConfirmationModal(bundleId, isAddon = false) {
            this.bundleId = bundleId;
            if (isAddon) {
                this.activateAndLoadAddonModal(bundleId);
            } else {
                this.isPackageButtonClicked = true;
                this.packageAction = "activate";
            }
        },

        /**
         * Open deactivate confirmation modal
         */
        openDeactivateConfirmationModal(bundleId) {
            this.bundleId = bundleId;
            this.isPackageButtonClicked = true;
            this.packageAction = "deactivate";
        },

        /**
         * Close confirmation modal
         */
        closeConfirmationModal() {
            this.isPackageButtonClicked = false;
        },

        /**
         * Redirect user to recharge-credit page
         */
        redirectAccepted() {
            return router.push('recharge-credit');
        },
    },

    mounted() {
        this.getPackages();
    },
};
